// Canonical CLI dispatch for the `vortex` headless runner — the single source
// of truth shared by every `bin/vortex` entry point (the plugin's own
// `scripts/vortex-cli.mjs` and `@vortex-os/base`'s `bin/vortex.mjs`). Keeping
// the dispatch here, inside the bundled library, means base's bin is a thin
// wrapper and there is exactly one place that knows how to:
//   - build the ritual registry (lazily lighting up `/recall` only when the
//     optional `@vortex-os/memory-extended` add-on is installed),
//   - run a slash command and print its JSON result,
//   - run the `session-start` / `session-end` ritual subcommands,
//   - print `--list` / `--help`.
//
// The agent-mediated integration layer for Claude Code (and any agent host):
// a `.claude/commands/*.md` prompt tells the agent to run a subcommand here,
// then the agent presents/judges the JSON result. This keeps the *judgment*
// with the agent (no API key, no programmatic LLM invoker) while the
// deterministic work — search, read, synthesize from existing records — runs
// here over the @vortex-os libraries.
//
// Result JSON / reports go to stdout; diagnostics and errors go to stderr.

import { execFileSync, spawn } from "node:child_process";
import {
  existsSync,
  readFileSync,
  readdirSync,
  writeFileSync,
  mkdirSync,
  openSync,
  writeSync,
  closeSync,
  linkSync,
  rmSync,
  statSync,
  utimesSync,
} from "node:fs";
import { createHash } from "node:crypto";
import { createRequire } from "node:module";
import { hostname, homedir, setPriority, constants as osConstants } from "node:os";
import { isAbsolute, join } from "node:path";
import {
  makeContext,
  loadVortexConfig,
  resolveEnvironment,
  type ModuleContext,
  type VortexConfig,
} from "@vortex-os/core";
import {
  runSlash,
  runSlashArgv,
  CommandNotFoundError,
  type CommandRegistry,
} from "@vortex-os/slash-commands";
import { createRitualRegistry } from "./registry.js";
import {
  runArchiveSync,
  archiveRelPath,
  gitOut,
  gitNet,
  detectInterruptedGitOp,
  reclaimStaleGitIndexLock,
  isArchiveOnlyCommit,
  type ArchiveSyncResult,
} from "./archive-sync.js";
import { parseHookStdin, readHookStream, waitForTranscriptStability, liveSkipForFinalize } from "./finalize.js";
import { pruneHandoffs } from "./handoff.js";
import { resolveTemplatesDir } from "./commands/vortex.js";
import { autoReindexMemory } from "./commands/reindex.js";
import { frameworkBookkeepingPrefix, runTemplatesUpdate } from "./update.js";
import { checkBaseUpdate, readInstalledBaseVersion } from "./update-check.js";
import { checkAddonUpdates } from "./addon-check.js";
import {
  isInstanceRoot,
  readGlobalInstancePointer,
  inspectGlobalSetup,
} from "./global-setup.js";
import {
  collectSessionStartReport,
  renderSessionStartReport,
  detectWorklogGaps,
  countUncommitted,
  gapWindowSinceArg,
} from "./session-start-report.js";
import {
  runCurateAccept,
  runCurateCandidates,
  runCurateDecline,
  runCuratePreview,
  type CuratePayload,
} from "./curate-cli.js";

/**
 * Subcommands of the `/vortex` root command that are exposed as top-level CLI
 * shortcuts: `npx vortex init` dispatches `/vortex init`. Keeps the documented
 * `npx vortex init` / `status` / `doctor` working without the user typing the
 * `vortex vortex` double word.
 */
/**
 * The record categories the deterministic ontos rebuild consumes — MUST mirror the
 * SOURCES list in `@vortex-os/ontos/scripts/rebuild-ontos.mjs` (flat dirs, top-level
 * `.md` files only), or the freshness check misses/over-fires. Worklogs are NOT
 * rebuild inputs, so they don't gate it.
 */
const ONTOS_RECORD_DIRS = ["_failures", "decision-log", "_memory", "runbooks", "ontos"] as const;

/**
 * Cheap staleness fingerprint over the ontos rebuild's record sources (a few hundred
 * stats, single-digit ms): newest mtime + file count + a hash of the sorted path set,
 * so an EDIT (mtime), a DELETE (count/paths) and a RENAME (paths) all change it — a
 * newest-mtime-only signal missed deletions (Codex review). Mirrors the rebuild
 * script's own listing rules: flat, `.md` only, `_INDEX.md` skipped. Best-effort:
 * unreadable dirs/files just don't contribute. (A restored old file with an old mtime
 * AND an unchanged path set is the one accepted blind spot — rare, self-heals on the
 * next record change.)
 */
function ontosRecordsFingerprint(dataDir: string): string {
  let newest = 0;
  let files = 0;
  const paths: string[] = [];
  for (const d of ONTOS_RECORD_DIRS) {
    let entries;
    try {
      entries = readdirSync(join(dataDir, d), { withFileTypes: true });
    } catch {
      continue; // dir absent — skip
    }
    for (const e of entries) {
      if (!e.isFile() || !e.name.endsWith(".md") || e.name === "_INDEX.md") continue;
      try {
        const m = statSync(join(dataDir, d, e.name)).mtimeMs;
        if (m > newest) newest = m;
        files++;
        paths.push(`${d}/${e.name}`);
      } catch {
        /* raced/unreadable file — skip */
      }
    }
  }
  const pathsHash = createHash("sha256").update(paths.sort().join("\n")).digest("hex").slice(0, 16);
  return `${Math.floor(newest)}:${files}:${pathsHash}`;
}

/**
 * Resolve the installed ontos rebuild script. Package-resolution first (hoisted /
 * pnpm / unusual layouts — Codex review), with the conventional instance-local
 * node_modules path as the fallback; null when the add-on (or an older version
 * without the script) doesn't ship it.
 */
function resolveOntosRebuildScript(repoRoot: string): string | null {
  try {
    const req = createRequire(join(repoRoot, "package.json"));
    return req.resolve("@vortex-os/ontos/scripts/rebuild-ontos.mjs");
  } catch {
    const p = join(repoRoot, "node_modules", "@vortex-os", "ontos", "scripts", "rebuild-ontos.mjs");
    return existsSync(p) ? p : null;
  }
}

const VORTEX_SUBCOMMANDS = ["init", "status", "import", "doctor", "update", "sync", "global-setup", "ontos"] as const;

export interface CliIo {
  /** Write a chunk to stdout. Default: `process.stdout.write`. */
  readonly stdout?: (s: string) => void;
  /** Write a chunk to stderr. Default: `process.stderr.write`. */
  readonly stderr?: (s: string) => void;
}

/**
 * Build the ritual registry, lighting up `/recall` ONLY when the optional
 * `@vortex-os/memory-extended` add-on is installed alongside base.
 *
 * Base ships without the add-on (it is `external` in the tsup bundle), so this
 * probe is a real `await import` that throws `ERR_MODULE_NOT_FOUND` on a lean
 * install — caught here so the CLI runs with base alone and never throws on
 * startup. When the add-on IS present, its local embedder wires up `/recall`.
 * `curate` is intentionally NOT wired here — it is agent-mediated.
 */
export async function buildRegistry(): Promise<CommandRegistry> {
  try {
    const { vector } = await import("@vortex-os/memory-extended");
    return createRitualRegistry({ recall: { embed: vector.createLocalEmbedder() } });
  } catch {
    // Add-on absent (lean base install) — recall stays off; everything else works.
    return createRitualRegistry();
  }
}

/**
 * Resolve the repo root every entry point should use. Precedence:
 *   1. `VORTEX_REPO_ROOT` env — explicit override always wins.
 *   2. cwd, if cwd is itself an initialized instance — running inside an instance
 *      uses that instance (so multiple instances on one machine still work).
 *   3. the global pointer (`~/.claude/vortex-global.json`), set by
 *      `vortex global-setup` — so commands run from any other folder record to
 *      the one central instance.
 *   4. cwd — the original fallback.
 * Until global-setup runs (no pointer), this is identical to the old behavior:
 * env override, else cwd.
 */
export function resolveRepoRoot(): string {
  const override = process.env.VORTEX_REPO_ROOT?.trim();
  if (override) return override;
  const cwd = process.cwd();
  if (isInstanceRoot(cwd)) return cwd;
  const pointer = readGlobalInstancePointer();
  if (pointer) return pointer;
  return cwd;
}

/**
 * Wrap a shell-supplied argv token that contains whitespace in quotes, so the
 * value stays visible as a single quoted span in the reassembled command string.
 * Used only by `argvToSlash` for the non-`/vortex` commands; `/vortex` threads
 * its tokens directly (see `runVortexCli`) and never round-trips through here.
 * Note: the non-`/vortex` runner splits on whitespace only (it is not
 * quote-aware), so this is best-effort preservation of the pre-existing
 * behavior, not a guarantee of perfect token reunification.
 */
function requote(token: string): string {
  if (!/\s/.test(token)) return token;
  if (token.includes('"') && !token.includes("'")) return `'${token}'`;
  return `"${token.replace(/"/g, '')}"`;
}

/**
 * Reassemble shell-split argv into the `/`-command string `runSlash` expects,
 * for every command EXCEPT `/vortex`. Whitespace-containing values are wrapped by
 * `requote` so they stay visible as a quoted span, and empty tokens are dropped.
 * `runSlash` then splits this on whitespace only (it is NOT quote-aware), so this
 * path preserves the long-standing behavior of those commands rather than
 * guaranteeing perfect token reunification.
 *
 * `/vortex` deliberately bypasses this: the CLI threads its tokens straight to
 * the handler (`runSlashArgv` in runVortexCli), which is what fixes the
 * quoted/empty-value boundary bug — no lossy join-then-resplit round-trip.
 */
export function argvToSlash(argv: readonly string[]): string {
  const name = argv[0] ?? "";
  const body = argv.slice(1).map(requote).join(" ");
  return `/${name} ${body}`.trim();
}

/**
 * Read a curate proposal payload from CLI args. Three sources, in order:
 *   --payload <json>        — inline JSON string (one shell token)
 *   --payload-file <path>   — JSON file (robust for large bodies)
 *   stdin                   — JSON piped in (fd 0), the heredoc-friendly path
 * Throws a clear Error if no source yields parseable JSON, so the caller's
 * try/catch reports `[vortex] error: ...` rather than a raw JSON.parse stack.
 */
function readCuratePayload(args: readonly string[]): CuratePayload {
  let raw: string | null = null;
  for (let i = 0; i < args.length; i++) {
    const t = args[i];
    if (t === "--payload" && i + 1 < args.length) {
      raw = args[++i]!;
      break;
    }
    if (t === "--payload-file" && i + 1 < args.length) {
      raw = readFileSync(args[++i]!, "utf8");
      break;
    }
  }
  if (raw === null) {
    // No inline/file payload — read stdin. Synchronous read of fd 0 keeps the
    // dispatch flat; an empty stdin yields a clear error below.
    try {
      raw = readFileSync(0, "utf8");
    } catch {
      raw = "";
    }
  }
  if (!raw || raw.trim().length === 0) {
    throw new Error(
      "curate proposal payload required — pass --payload '<json>', --payload-file <path>, or pipe JSON on stdin.",
    );
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    throw new Error(`curate payload is not valid JSON: ${(e as Error).message}`);
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    throw new Error("curate payload must be a JSON object.");
  }
  return parsed as CuratePayload;
}

/**
 * Run the `vortex` CLI for the given argv (already sliced past `node script`).
 * Returns the process exit code (0 success, 1 on error) instead of calling
 * `process.exit`, so callers/tests stay in control.
 */
export async function runVortexCli(argv: readonly string[], io?: CliIo): Promise<number> {
  const out = io?.stdout ?? ((s: string) => process.stdout.write(s));
  const err = io?.stderr ?? ((s: string) => process.stderr.write(s));

  try {
    // `vortex statusline [lite|install]` — the Claude Code statusLine renderer
    // (and its settings wiring). Hot path: Claude Code re-runs this on every
    // statusline refresh, so it dispatches FIRST — before the repo-root
    // resolver and the registry/embedder probe — and the module loads lazily.
    if (argv[0] === "statusline") {
      const { runStatuslineCli } = await import("./statusline.js");
      // `install` targets the CURRENT project (env override honored) — NOT the
      // global-pointer instance resolveRepoRoot() may select: a status bar
      // belongs to the folder you run the command in.
      const statuslineRoot = process.env.VORTEX_REPO_ROOT?.trim() || process.cwd();
      return runStatuslineCli(argv.slice(1), statuslineRoot, out, err);
    }

    // `vortex guard write` — the PreToolUse hook body (deny literal control
    // bytes in to-be-written text). Hot path like statusline: fires on every
    // file-writing tool call, so it dispatches before the repo-root resolver
    // and the registry probe; the module loads lazily and always exits 0.
    if (argv[0] === "guard") {
      const { runGuardCli } = await import("./guard.js");
      return runGuardCli(argv.slice(1), out, err);
    }

    const repoRoot = resolveRepoRoot();

    // `vortex failure record|list` — the failure ledger (`data/_failures/`),
    // the evidence store of the self-improvement loop (see failures.ts).
    // Deterministic, no LLM, no registry; results are JSON on stdout. The
    // ledger roots at the NEAREST instance above cwd (same upward marker walk
    // as the guard — an agent may run this from a subfolder), falling back to
    // the normal resolver (global pointer et al.) so it also works from any
    // folder `global-setup` covers.
    if (argv[0] === "failure") {
      const { runFailureCli } = await import("./failures.js");
      const { resolveInstanceRoot } = await import("./guard.js");
      const root = resolveInstanceRoot(process.cwd()) ?? repoRoot;
      const ctx = makeContext(root);
      return runFailureCli(argv.slice(1), ctx.dataDir, out, err);
    }

    // `vortex source-map index|find|stats` — queryable map of the user's own
    // source code (generic solution/system/feature/kind columns; targets from
    // `.agent/vortex.json` → sourceMap). Deterministic, no LLM, no registry;
    // loaded lazily (it imports node:sqlite, Node >= 22.5 — the command reports
    // a clear error on older runtimes instead of breaking the whole CLI). Roots
    // at the nearest instance like `failure` (agents may run it from subfolders).
    if (argv[0] === "source-map") {
      const { runSourceMapCli } = await import("./source-map.js");
      const { resolveInstanceRoot } = await import("./guard.js");
      const root = resolveInstanceRoot(process.cwd()) ?? repoRoot;
      return runSourceMapCli(argv.slice(1), root, out, err);
    }

    // Ritual subcommands that run a hook-style report rather than a slash
    // command. These do NOT need the registry/embedder, so probe is skipped.
    if (argv[0] === "session-start") {
      await runSessionStart(repoRoot, out);
      return 0;
    }
    if (argv[0] === "session-end") {
      // Hand the real work to a detached child and return at once, so the
      // host's teardown has nothing to cancel (the inline run died with a
      // noisy "Hook cancelled" on every Ctrl+D quit — see session-end-detach).
      // TTY stdin / the child's own env marker run the original inline path.
      // The payload is read ONCE here and travels down BOTH arms, so the
      // inline fallback after a failed hand-off keeps the finalize signal.
      const { sessionEndShouldDetach, readStdinBounded, relaunchSessionEndDetached } = await import(
        "./session-end-detach.js"
      );
      if (sessionEndShouldDetach()) {
        const payload = await readStdinBounded();
        if (await relaunchSessionEndDetached(repoRoot, payload)) return 0;
        await runSessionEnd(repoRoot, out, payload);
        return 0;
      }
      await runSessionEnd(repoRoot, out);
      return 0;
    }
    if (argv[0] === "session-tick") {
      await runSessionTick(repoRoot, out);
      return 0;
    }

    // `vortex catch-up` — explicit archive sync (ingest closed sessions, sweep
    // raw, auto-commit, opt-in push). The agent's first step when the user asks
    // to sync; also handy manually. No registry/embedder probe needed.
    if (argv[0] === "catch-up") {
      await runCatchUpCli(repoRoot, out);
      return 0;
    }

    // `vortex check-updates` — on-demand network check for a newer
    // @vortex-os/base (the agent's re-check after "not now", and the post-
    // install confirmation). Read-only, no registry/embedder needed; forces the
    // check on regardless of `updates.check` since the user invoked it. Result
    // JSON to stdout; the apply path stays the consent-gated agent + `vortex
    // update`. Offline/failure → latest:null / newer:false (never throws).
    if (argv[0] === "check-updates") {
      const result = checkBaseUpdate(makeContext(repoRoot));
      out(JSON.stringify(result, null, 2) + "\n");
      return 0;
    }

    // `vortex vectorize` — build/refresh the semantic-recall index, downloading
    // the embedding model on first run. Doubles as the background worker that
    // session start spawns to set recall up the first time the add-on is present
    // (see `runSessionStart`). No registry/embedder probe needed.
    //
    // `vortex vectorize --refresh` — the INCREMENTAL background worker session
    // start spawns every session once the index exists: a cache-only pass that
    // folds newly-archived sessions / changed memories into the EXISTING index
    // in place (no model download, no temp-db publish). Detached from the hook so
    // a heavy user's large per-session backlog never blocks session start.
    if (argv[0] === "vectorize") {
      // This is the heavy background indexer (loads the ~2 GB embedding model and
      // pegs a core while it embeds). It MUST never starve the desktop/compositor:
      // on a low-RAM / integrated-GPU machine an inline-priority run during a large
      // first-time backlog can make the UI stutter or hang. Drop to BELOW-NORMAL OS
      // priority so it always yields to the foreground — the resumable index design
      // makes a slower (or paused) worker safe, it never loses progress. Best-effort
      // (setPriority can EPERM on some hosts); failure just keeps normal priority.
      try {
        setPriority(osConstants.priority.PRIORITY_BELOW_NORMAL);
      } catch {
        /* best effort — keep going at normal priority */
      }
      if (argv.includes("--refresh")) {
        await runVectorizeRefresh(repoRoot, out, err);
      } else {
        await runVectorizeSetup(repoRoot, out, err);
      }
      return 0;
    }

    // Curate value-loop subcommands — deterministic, no LLM, no registry.
    // The agent makes the judgment; these do the safe filesystem work. Result
    // JSON to stdout. curate-candidates lists existing docs to choose among;
    // the rest take a serializable proposal payload (--payload <json> or stdin)
    // and validate/preview/write/decline it.
    if (argv[0] === "curate-candidates") {
      const result = await runCurateCandidates(repoRoot);
      out(JSON.stringify(result, null, 2) + "\n");
      return 0;
    }
    if (
      argv[0] === "curate-preview" ||
      argv[0] === "curate-accept" ||
      argv[0] === "curate-decline"
    ) {
      const payload = readCuratePayload(argv.slice(1));
      const result =
        argv[0] === "curate-preview"
          ? await runCuratePreview(repoRoot, payload)
          : argv[0] === "curate-accept"
            ? await runCurateAccept(repoRoot, payload)
            : await runCurateDecline(repoRoot, payload);
      out(JSON.stringify(result, null, 2) + "\n");
      return 0;
    }

    const registry = await buildRegistry();

    if (argv.length === 0 || argv[0] === "--list" || argv[0] === "--help") {
      const names = registry
        .list()
        .map((c) => `  ${c.name}  —  ${c.description}`)
        .join("\n");
      err(
        `vortex — headless ritual runner\n\nCommands:\n${names}\n` +
          `  session-start  —  emit the start-of-session boot report (git pull + data counts + archive sync)\n` +
          `  session-end    —  best-effort archive sync at wind-down, incl. the ending session itself when the host pipes its hook payload (crash-safe — session-start repeats it)\n` +
          `  session-tick   —  mid-session archive streaming (Stop hook; opt-in via autoRecord.archiveTickMinutes — throttled, archive-only push, never pulls)\n` +
          `  catch-up       —  explicit archive sync now (ingest closed sessions, sweep raw copies, auto-commit, opt-in push); run before a manual sync\n` +
          `  check-updates  —  check the npm registry for a newer @vortex-os/base (read-only; prints the exact update command)\n` +
          `  statusline     —  render the Claude Code status bar from stdin JSON (\`lite\` for 1-line; \`install [--lite]\` wires .claude/settings.json)\n` +
          `  failure        —  failure ledger (\`record\` an occurrence by recurrence key / \`list\` open groups with their escalation stage)\n` +
          `  source-map     —  queryable map of your own source code (\`index\` rebuilds from .agent/vortex.json sourceMap.targets; \`find <term>\` answers "does my X do Y? where?"; \`stats\`)\n` +
          `  guard          —  deterministic hooks (\`write\` = PreToolUse control-byte deny; \`failure-admission\` = Stop self-admission nudge; wired by \`vortex init\`)\n\n` +
          `Instance shortcuts (also available as \`/vortex <sub>\`):\n` +
          `  init   —  first-time setup: routers + data/ + hooks + slash-commands\n` +
          `  status —  instance state report\n` +
          `  import —  bring an existing notes folder into data/\n` +
          `  doctor —  health diagnosis\n` +
          `  update —  refresh framework templates from the installed package (hash-guarded; --dry-run to preview; --adopt <path> to re-adopt a file you edited)\n\n` +
          `Usage: vortex <command> [args...]\n`,
      );
      return 0;
    }

    // `vortex init|status|import|doctor|update|sync` are shortcuts for the
    // `/vortex` root command's subcommands — so `npx vortex init` works as
    // documented, not just `npx vortex vortex init`. Anything else dispatches as
    // the slash command of the same name (e.g. `vortex agenda` → `/agenda`).
    const name = argv[0]!;
    const isVortexSub = (VORTEX_SUBCOMMANDS as readonly string[]).includes(name);
    const context = makeContext(repoRoot);

    if (isVortexSub || name === "vortex") {
      // Both forms target `/vortex`. Thread the already-split tokens straight to
      // the handler rather than rebuilding a string the runner would re-split —
      // that round-trip is lossy (a quoted/empty value can shift later token
      // boundaries and swallow a following flag like `--force`). The shorthand
      // keeps the full argv (argv[0] is the subcommand); the explicit
      // `vortex vortex …` form drops the leading `vortex` word.
      const subArgv = isVortexSub ? argv : argv.slice(1);
      const result = await runSlashArgv("vortex", subArgv, { registry, context });
      out(JSON.stringify(result, null, 2) + "\n");
      return 0;
    }

    // Every other command goes through the whitespace-split runner; they don't
    // re-tokenize, so `argvToSlash` re-quotes spaced values and drops empties.
    const result = await runSlash(argvToSlash(argv), { registry, context });
    out(JSON.stringify(result, null, 2) + "\n");
    return 0;
  } catch (e) {
    if (e instanceof CommandNotFoundError) {
      err(
        `[vortex] unknown command: ${e.message}\nRun \`vortex --list\` to see available commands.\n`,
      );
    } else {
      err(`[vortex] error: ${(e as Error)?.message ?? e}\n`);
    }
    return 1;
  }
}

// ---------------------------------------------------------------------------
// Auto-vectorize (recall setup) — first-time, non-blocking, opt-out-able.
// ---------------------------------------------------------------------------

/** What session start should do about vectorization. See {@link decideVectorizeAction}. */
export type VectorizeAction = "spawn-incremental" | "spawn-setup" | "none";

/**
 * Decide what session start does about vectorization. Pure (no fs / network /
 * child process) so the gate logic is unit-testable in isolation:
 *  - `vectorize` off                       → `"none"`.
 *  - a vectorize worker already holds the shared lock (first-time build OR a
 *    prior incremental refresh still running) → `"none"`, so back-to-back
 *    sessions never stack workers on the index.
 *  - the recall index already exists       → `"spawn-incremental"` (DETACHED
 *    background worker folds new sessions/memories in — incremental, cache-only,
 *    no download; NEVER blocks session start. This used to run inline and
 *    `await`ed, which hung session start for minutes on a heavy corpus.)
 *  - index missing, but the add-on is present and auto-download is allowed →
 *    `"spawn-setup"` (detached first-time build + model download — installing the
 *    add-on is the opt-in).
 *  - anything else (no add-on, auto-download off)                → `"none"`.
 */
export function decideVectorizeAction(flags: {
  readonly vectorizeOn: boolean;
  readonly dbExists: boolean;
  readonly autoDownloadOn: boolean;
  readonly addonPresent: boolean;
  readonly setupInProgress: boolean;
}): VectorizeAction {
  if (!flags.vectorizeOn) return "none";
  // A worker (first-time setup OR incremental refresh) already holds the shared
  // lock — skip spawning another, so two quick back-to-back sessions never stack
  // workers writing the same index.
  if (flags.setupInProgress) return "none";
  if (flags.dbExists) return "spawn-incremental";
  if (flags.autoDownloadOn && flags.addonPresent) return "spawn-setup";
  return "none";
}

/** Off-values for the `VORTEX_VECTORIZE_AUTO_DOWNLOAD` env override. */
const VECTORIZE_AUTODOWNLOAD_OFF = new Set(["0", "false", "off", "no"]);

/**
 * Whether the first-time model auto-download is enabled, combining the config
 * flag with an env override. The env var lets CI / metered shells force it off
 * without editing `vortex.json` (`VORTEX_VECTORIZE_AUTO_DOWNLOAD=0`).
 */
function vectorizeAutoDownloadEnabled(config: VortexConfig): boolean {
  if (!config.autoRecord.vectorizeAutoDownload) return false;
  const env = process.env.VORTEX_VECTORIZE_AUTO_DOWNLOAD;
  if (env !== undefined && VECTORIZE_AUTODOWNLOAD_OFF.has(env.trim().toLowerCase())) {
    return false;
  }
  return true;
}

/** Resolve-only presence check for the optional add-on; never imports it (no heavy deps pulled). */
function memoryExtendedPresent(): boolean {
  try {
    createRequire(import.meta.url).resolve("@vortex-os/memory-extended");
    return true;
  } catch {
    return false;
  }
}

/**
 * A lock whose mtime is older than this — i.e. it has gone this long WITHOUT a
 * heartbeat (see {@link acquireVectorizeLock}) — is treated as a crashed/hung
 * worker and reclaimed. Because the holder refreshes its lock mtime every
 * {@link VECTORIZE_HEARTBEAT_MS} while it works, this is "time since last
 * progress", not "time since acquire": a legitimately long build keeps its lock
 * fresh and is NEVER stolen, while a hung-but-alive worker — or a lock whose PID
 * was reused, so the dead-owner probe can't fire — is reclaimed in minutes
 * instead of hours (the previous 6h backstop). Kept comfortably above the
 * worst-case wall time of a single embed step (the memory path has no input cap
 * yet — see session.js's per-turn cap) so one slow item can't starve the
 * heartbeat past the TTL and let a second worker reclaim a live build.
 */
const VECTORIZE_LOCK_TTL_MS = 30 * 60 * 1000; // 30 min (was 6h; safe to shorten now the holder heartbeats)

/** How often a live lock holder refreshes its lock mtime — well under the TTL. */
const VECTORIZE_HEARTBEAT_MS = 2 * 60 * 1000; // 2 min

function vectorizeLockPath(ctx: ModuleContext): string {
  return join(ctx.dataDir, "_indexes", ".vectorize.lock");
}

/**
 * Whether the process that holds this lock is PROVABLY gone — so its lock can be
 * reclaimed at once, without waiting out the (deliberately long) TTL. The lock
 * token starts with the owner PID; if that PID is not alive, a killed worker
 * (a host-app quit before breakaway, machine sleep, a `kill`) never blocks the
 * next run for hours. Conservative: only a CLEARLY-dead PID reclaims early —
 * anything uncertain (unparseable token, PID alive, PID possibly reused, a
 * permission error) defers to the TTL. The lock is LOCAL (the recall index is a
 * local, git-ignored file), so the PID is always on this machine and the check is
 * meaningful.
 */
function vectorizeLockOwnerDead(lockPath: string): boolean {
  let pid: number;
  try {
    const token = readFileSync(lockPath, "utf8").trim();
    pid = Number.parseInt(token.split("-")[0] ?? "", 10);
  } catch {
    return false; // can't read/parse → don't claim dead; let the TTL decide
  }
  if (!Number.isInteger(pid) || pid <= 0 || pid === process.pid) return false;
  try {
    process.kill(pid, 0); // signal 0: existence probe, kills nothing
    return false; // alive
  } catch (e) {
    // ESRCH → no such process (dead → reclaim). EPERM → exists, not ours (alive).
    return (e as NodeJS.ErrnoException).code === "ESRCH";
  }
}

/**
 * Cheap "is a worker already running?" check for the session-start gate — only an
 * optimization to avoid spawning duplicate workers. NOT the correctness guard:
 * the worker itself acquires an atomic lock (see {@link acquireVectorizeLock}). A
 * lock whose owner PID is dead (or older than the TTL) is treated as NOT in
 * progress, so the gate still spawns a worker, which then reclaims the dead lock.
 */
function vectorizeSetupInProgress(ctx: ModuleContext): boolean {
  const lock = vectorizeLockPath(ctx);
  try {
    if (!existsSync(lock)) return false;
    if (Date.now() - statSync(lock).mtimeMs >= VECTORIZE_LOCK_TTL_MS) return false;
    if (vectorizeLockOwnerDead(lock)) return false;
    return true;
  } catch {
    return false;
  }
}

/** Handle for a held vectorize lock — its single job is to release ownership. */
interface VectorizeLock {
  /** Release OUR lock (idempotent, best-effort). */
  readonly release: () => void;
}

/**
 * Acquire the shared vectorize lock ATOMICALLY — the one correctness gate shared
 * by BOTH the first-time setup ({@link runVectorizeSetup}) and the incremental
 * refresh ({@link runVectorizeRefresh}), so the two can never write the index at
 * once and two refreshes can never overlap. Returns the lock handle, or `null`
 * when a non-stale lock is already held (another worker is running — the caller
 * skips). A lock is reclaimed when it is older than {@link VECTORIZE_LOCK_TTL_MS}
 * OR its owner PID is provably dead (see {@link vectorizeLockOwnerDead}) — so a
 * killed worker never blocks the next run for hours. The lock file carries a
 * unique OWNERSHIP token: a worker whose stale lock was reclaimed by a newer
 * worker will see a different token (or none) on release and never delete the
 * newer worker's lock.
 */
export function acquireVectorizeLock(ctx: ModuleContext): VectorizeLock | null {
  const lockPath = vectorizeLockPath(ctx);
  mkdirSync(join(ctx.dataDir, "_indexes"), { recursive: true });
  const token = `${process.pid}-${process.hrtime.bigint()}`;
  let lockFd: number;
  try {
    lockFd = openSync(lockPath, "wx");
  } catch (e) {
    if ((e as NodeJS.ErrnoException).code !== "EEXIST") throw e;
    let stale = true;
    try {
      const ttlExpired = Date.now() - statSync(lockPath).mtimeMs >= VECTORIZE_LOCK_TTL_MS;
      // Reclaim early if the owner crashed (PID gone), else wait out the TTL.
      stale = ttlExpired || vectorizeLockOwnerDead(lockPath);
    } catch {
      stale = true; // unreadable lock → treat as stale
    }
    if (!stale) return null; // another worker holds a live lock
    rmSync(lockPath, { force: true });
    try {
      lockFd = openSync(lockPath, "wx");
    } catch {
      return null; // lost the reclaim race to another worker
    }
  }
  // We created the lock file. Stamp our token so release only ever removes a lock
  // that still belongs to us. If the stamp itself fails, clean up our empty lock
  // and report "could not acquire" rather than leaking it until the TTL.
  try {
    writeSync(lockFd, token + "\n");
  } catch {
    closeSync(lockFd);
    rmSync(lockPath, { force: true });
    return null;
  }
  closeSync(lockFd); // close so the lock file can be deleted later (Windows)
  // HEARTBEAT: while we hold the lock, refresh its mtime periodically so "lock age"
  // tracks "time since last progress", not "time since acquire" — this is what makes
  // the short TTL safe (a live build keeps touching the file; a hung/crashed one
  // stops, goes stale, and is reclaimed). The interval is unref()'d so it never keeps
  // the worker process alive on its own, and stops PERMANENTLY the instant the lock
  // file is gone or no longer carries our token (a newer owner reclaimed a stale
  // lock) — so we can never touch, and thus prolong, a lock that is no longer ours.
  const heartbeat = setInterval((): void => {
    try {
      const cur = existsSync(lockPath) ? readFileSync(lockPath, "utf8").trim() : "";
      if (cur !== token) {
        clearInterval(heartbeat);
        return;
      }
      const now = new Date();
      utimesSync(lockPath, now, now);
    } catch {
      clearInterval(heartbeat); // lock gone / unreadable → stop touching it
    }
  }, VECTORIZE_HEARTBEAT_MS);
  heartbeat.unref(); // the heartbeat must never, by itself, keep the worker alive
  return {
    release: (): void => {
      clearInterval(heartbeat); // stop heartbeating before we let the lock go
      try {
        const cur = existsSync(lockPath) ? readFileSync(lockPath, "utf8").trim() : "";
        // Only OUR token. An empty file (a newer worker reclaimed a stale lock and
        // hasn't stamped yet) or a different token is never ours to delete.
        if (cur === token) rmSync(lockPath, { force: true });
      } catch {
        /* best effort — a stuck lock is reclaimed after the TTL */
      }
    },
  };
}

/**
 * Launch a detached background `vortex` worker (the SAME node + bin entry +
 * `args`, never `npx` which could drift PATH / package resolution) that OUTLIVES
 * the short-lived session-start hook AND, on Windows, survives the host app
 * (e.g. Claude Code) being fully quit.
 *
 * `spawn(..., { detached, stdio: "ignore" })` + `unref()` already lets the worker
 * outlive its immediate spawner. But on Windows a host app may put its whole
 * descendant tree in a Job Object with kill-on-close, and Node exposes no
 * `CREATE_BREAKAWAY_FROM_JOB` flag — so a plain detached child can still be killed
 * when the app exits. To break out of that job we have the WMI service create the
 * process: a `Win32_Process.Create` child is parented to WmiPrvSE, NOT to us, so
 * it is outside our job entirely. PowerShell issues the one CIM call and exits;
 * the worker runs on regardless. On ANY failure (no PowerShell, CIM error) we
 * fall back to the plain detached spawn, so behavior is never WORSE than before.
 *
 * CRITICAL (2026-06-29, empirically reproduced): this PowerShell is spawned
 * NON-detached. A `{ detached: true }` PowerShell on Windows silently exits 0
 * WITHOUT running the command (no console to attach), which (a) never issued the
 * CIM call — so NO worker was ever created — and (b) because it exited 0, the
 * exit-code fallback below treated it as success and never ran plainSpawn either.
 * Net effect: the recall index worker never spawned at session start at all (the
 * "index silently stops building" bug). Non-detached, the one-shot CIM call runs
 * (~sub-second) and the WMI-parented worker survives regardless of this short
 * PowerShell's lifetime; `unref()` keeps it off the hook's critical path.
 *
 * The WMI child inherits the WMI service's environment, not ours, so we do NOT
 * rely on `VORTEX_REPO_ROOT`: the worker's CurrentDirectory is set to `repoRoot`
 * and `resolveRepoRoot()` recovers it from cwd (an instance root). The plain
 * fallback still passes the env, belt-and-suspenders. `windowsHide` keeps any
 * transient PowerShell off-screen.
 */
function spawnDetachedWorker(repoRoot: string, args: readonly string[]): void {
  const node = process.execPath;
  const script = process.argv[1] ?? "";
  const plainSpawn = (): void => {
    const child = spawn(node, [script, ...args], {
      cwd: repoRoot,
      env: { ...process.env, VORTEX_REPO_ROOT: repoRoot },
      detached: true,
      stdio: "ignore",
      windowsHide: true,
    });
    // Swallow a spawn failure (e.g. ENOENT) so it never throws an unhandled
    // 'error' event; the next session simply retries, or a manual run works.
    child.on("error", () => {});
    child.unref();
  };

  if (process.platform !== "win32") {
    plainSpawn();
    return;
  }

  // Windows: break out of any kill-on-close Job Object via WMI process creation.
  try {
    const quoteArg = (s: string): string => `"${s.replace(/"/g, '\\"')}"`;
    const commandLine = [node, script, ...args].map(quoteArg).join(" ");
    const psLit = (s: string): string => `'${s.replace(/'/g, "''")}'`;
    // CreateFlags = DETACHED_PROCESS (0x8). Without it, WMI's Win32_Process.Create
    // gives the console-subsystem child (node) a NEW CONSOLE WINDOW — the visible
    // black popup that flashes at every session start (one per session, so several
    // open terminals show several windows). DETACHED_PROCESS starts the worker with
    // NO console, so no window, while leaving the WMI parentage (the job-breakaway
    // that is the whole reason we use WMI here) completely untouched. The worker
    // MUST NOT depend on stdio: with no console its standard handles may be
    // null/invalid, so a future worker that relied on stdout/stderr would lose it
    // (node tolerates writes to them without crashing — verified). Today's worker is
    // fine: its real work is sqlite + files, and its console.* output is diagnostic
    // only. NOTE: the
    // intuitive CREATE_NO_WINDOW (0x08000000) is NOT usable — Win32_Process.Create
    // rejects it with ReturnValue 21 (invalid parameter); DETACHED_PROCESS is the one
    // WMI-accepted CreateFlags value that suppresses the console. (Empirically verified
    // 2026-06-30, Windows 11: flag 8 -> RET 0, worker runs, no window; flag 0x08000000
    // -> RET 21, no process. A non-zero RET still falls back to the hidden plainSpawn.)
    const psScript =
      `$si = New-CimInstance -ClassName Win32_ProcessStartup -ClientOnly ` +
      `-Property @{ CreateFlags = [uint32]8 }; ` +
      `$r = Invoke-CimMethod -ClassName Win32_Process -MethodName Create ` +
      `-Arguments @{ CommandLine = ${psLit(commandLine)}; CurrentDirectory = ${psLit(repoRoot)}; ProcessStartupInformation = $si }; ` +
      `exit [int]$r.ReturnValue`;
    // NON-detached on purpose — a detached PowerShell no-ops on Windows (see the
    // doc-comment above). The CIM call is a quick one-shot; the WMI-created worker
    // is breakaway and outlives this PowerShell either way.
    const ps = spawn(
      "powershell.exe",
      ["-NoProfile", "-NonInteractive", "-Command", psScript],
      { stdio: "ignore", windowsHide: true },
    );
    // No PowerShell (ENOENT) → fall back. A non-zero CIM ReturnValue (WMI blocked)
    // → fall back, so the refresh still runs (in-job, but it runs).
    ps.on("error", () => plainSpawn());
    ps.on("exit", (code) => {
      if (code !== 0) plainSpawn();
    });
    ps.unref();
  } catch {
    plainSpawn();
  }
}

/**
 * Launch the first-time recall setup in the background and return immediately.
 * Detached + job-breakaway via {@link spawnDetachedWorker} so it outlives both the
 * session-start hook and a host-app quit.
 */
function spawnVectorizeSetup(repoRoot: string): void {
  spawnDetachedWorker(repoRoot, ["vectorize"]);
}

/**
 * Launch the INCREMENTAL recall refresh in the background and return immediately.
 * Runs the cache-only `vortex vectorize --refresh` pass against the EXISTING index
 * — folds newly-archived sessions / changed memories in WITHOUT ever blocking
 * session start. This replaced the old inline `await vectorizeIndex(...)`: for a
 * heavy user a single session can add thousands of chunks, so embedding them on
 * the session-start critical path hung the prompt for minutes. The worker takes
 * the same atomic lock as setup (never overlaps a build or another refresh), a
 * crashed worker's lock is reclaimed after the TTL, and — via
 * {@link spawnDetachedWorker} — it survives a host-app quit on Windows.
 */
function spawnVectorizeRefresh(repoRoot: string): void {
  spawnDetachedWorker(repoRoot, ["vectorize", "--refresh"]);
}

// ---------------------------------------------------------------------------
// Auto-download the computer-use neural-TTS voice model — first-time,
// non-blocking, opt-out-able. Mirrors the recall model auto-download above:
// installing the `@vortex-os/computer-use` add-on is itself the opt-in, so the
// higher-quality neural voice becomes the default without the user ever running
// the fetch command. The fetch runs detached and never blocks session start;
// until it lands the voice falls back to the built-in Windows voice.
// ---------------------------------------------------------------------------

/** What session start should do about the neural-voice model. */
export type TtsDownloadAction = "spawn-setup" | "none";

/**
 * Decide whether session start kicks off the neural-voice model download. Pure
 * (no fs / network / child process) so the gate is unit-testable in isolation:
 *  - auto-download off                         → `"none"`.
 *  - model already cached                      → `"none"`.
 *  - add-on present, auto-download on, model
 *    missing, no download already running      → `"spawn-setup"`.
 *  - anything else                             → `"none"`.
 */
export function decideTtsDownloadAction(flags: {
  readonly autoDownloadOn: boolean;
  readonly modelPresent: boolean;
  readonly addonPresent: boolean;
  readonly setupInProgress: boolean;
}): TtsDownloadAction {
  if (!flags.autoDownloadOn) return "none";
  if (flags.modelPresent) return "none";
  if (flags.addonPresent && !flags.setupInProgress) return "spawn-setup";
  return "none";
}

/** Off-values for the `VORTEX_CU_TTS_AUTO_DOWNLOAD` env override. */
const TTS_AUTODOWNLOAD_OFF = new Set(["0", "false", "off", "no"]);

/**
 * Whether the first-time neural-voice model auto-download is enabled, combining
 * the config flag with an env override (`VORTEX_CU_TTS_AUTO_DOWNLOAD=0` for
 * CI / metered shells, mirroring the recall flag).
 */
function ttsAutoDownloadEnabled(config: VortexConfig): boolean {
  if (!config.autoRecord.ttsAutoDownload) return false;
  const env = process.env.VORTEX_CU_TTS_AUTO_DOWNLOAD;
  if (env !== undefined && TTS_AUTODOWNLOAD_OFF.has(env.trim().toLowerCase())) {
    return false;
  }
  return true;
}

/** Resolve-only presence check for the optional add-on; never imports it. */
function computerUsePresent(): boolean {
  try {
    createRequire(import.meta.url).resolve("@vortex-os/computer-use/package.json");
    return true;
  } catch {
    return false;
  }
}

/**
 * The Supertonic model cache directory — env override, else the per-user cache
 * the fetch script writes to (kept in sync with `fetch-supertonic.mjs` and
 * `speak-supertonic.mjs`, which resolve the same default).
 */
function supertonicModelDir(): string {
  return (
    process.env.VORTEX_CU_TTS_MODEL_DIR ||
    join(homedir(), ".vortex", "computer-use", "supertonic-3")
  );
}

/**
 * Is the neural-voice model fully present? The fetch worker writes a `.ready`
 * marker only after every file has landed, so its presence is the single
 * unambiguous "download complete" signal — the analog of the recall index's
 * final-DB existence check. A partial/interrupted fetch leaves no marker, so the
 * next session re-spawns and the idempotent fetch resumes. (Deleting model files
 * WITHOUT removing `.ready` is not auto-detected here — the runtime's own file
 * check then degrades to the built-in voice and a manual fetch repairs it;
 * checking the full file manifest here is deliberately avoided so this gate stays
 * decoupled from the model's file list.)
 */
function supertonicModelPresent(): boolean {
  try {
    return existsSync(join(supertonicModelDir(), ".ready"));
  } catch {
    return false;
  }
}

/**
 * Stale-lock TTL for the neural-voice download — the same generous 6 h as the
 * recall-setup lock (a slow/metered line can legitimately take a while for
 * ~380 MB); a lock older than this is treated as a crashed fetch and ignored.
 */
const TTS_DOWNLOAD_LOCK_TTL_MS = 6 * 60 * 60 * 1000;

function ttsDownloadLockPath(): string {
  return join(homedir(), ".vortex", "computer-use", ".supertonic-download.lock");
}

/**
 * Cheap "is a download already running?" check for the session-start gate — an
 * optimization to avoid spawning duplicate downloaders (the model cache is
 * machine-global, so a sibling instance may already be fetching). Best-effort: a
 * missing lock reads as "not running" and a stale lock (> TTL) is ignored.
 */
function ttsDownloadInProgress(): boolean {
  const lock = ttsDownloadLockPath();
  try {
    if (!existsSync(lock)) return false;
    return Date.now() - statSync(lock).mtimeMs < TTS_DOWNLOAD_LOCK_TTL_MS;
  } catch {
    return false;
  }
}

/**
 * Launch the first-time neural-voice model download in the background and return
 * immediately. Runs the add-on's own `fetch-supertonic.mjs` (resolved from the
 * installed package — no `npx`, no PATH drift) with this same node binary,
 * detached + `unref()` so it outlives this short-lived process; `stdio:"ignore"`
 * so it holds no pipes; `windowsHide` so no console flashes. A best-effort lock
 * touch narrows the concurrent-spawn window. Any failure is swallowed — a manual
 * `node …/fetch-supertonic.mjs` still works and the next session retries.
 */
function spawnTtsDownload(repoRoot: string): boolean {
  let fetchScript: string;
  try {
    fetchScript = createRequire(import.meta.url).resolve(
      "@vortex-os/computer-use/scripts/fetch-supertonic.mjs",
    );
  } catch {
    return false; // add-on not resolvable — nothing to spawn
  }
  // Best-effort lock so a sibling session/instance doesn't also spawn a fetch.
  try {
    mkdirSync(join(homedir(), ".vortex", "computer-use"), { recursive: true });
    writeFileSync(ttsDownloadLockPath(), String(Date.now()));
  } catch {
    // lock is only an optimization — proceed even if it can't be written
  }
  const child = spawn(process.execPath, [fetchScript], {
    cwd: repoRoot,
    env: { ...process.env, VORTEX_REPO_ROOT: repoRoot },
    detached: true,
    stdio: "ignore",
    windowsHide: true,
  });
  child.on("error", () => {});
  child.unref();
  return true;
}

/**
 * `vortex vectorize` — build the recall index, downloading the embedding model
 * if it is not cached. Safe to run concurrently with session starts:
 *   1. re-check the final index is absent (else exit — already set up);
 *   2. acquire an ATOMIC lock (`open … "wx"`; reclaim only a stale one) so two
 *      near-simultaneous runs can never both build;
 *   3. build into a temp db, then PUBLISH with an atomic, no-clobber hard link
 *      (`linkSync`) — so the inline path never observes a half-built index and a
 *      db a concurrent `/recall` published is never overwritten;
 *   4. on any failure, delete the temp db and leave the final index absent so
 *      the next session retries cleanly.
 */
async function runVectorizeSetup(
  repoRoot: string,
  out: (s: string) => void,
  err: (s: string) => void,
): Promise<void> {
  const ctx = makeContext(repoRoot);
  const indexDir = join(ctx.dataDir, "_indexes");
  const finalDb = join(indexDir, "memory.sqlite");

  if (existsSync(finalDb)) {
    out("recall index already present — nothing to do\n");
    return;
  }
  mkdirSync(indexDir, { recursive: true });

  // Acquire the shared atomic lock — `null` means another worker (a first-time
  // build OR an incremental refresh) already holds it, so we skip.
  const lock = acquireVectorizeLock(ctx);
  if (!lock) {
    out("another recall setup is already in progress — skipping\n");
    return;
  }

  const tmpDb = join(indexDir, `memory.sqlite.building-${process.pid}`);
  const tmpSidecars = [tmpDb + "-wal", tmpDb + "-shm", tmpDb + "-journal"];
  const cleanTmp = (): void => {
    rmSync(tmpDb, { force: true });
    for (const s of tmpSidecars) rmSync(s, { force: true });
  };

  // From here we hold the lock; the try/finally guarantees it is always released.
  try {
    // Re-check under the lock: a concurrent `/recall` may have just built it.
    if (existsSync(finalDb)) {
      out("recall index already present — nothing to do\n");
      return;
    }
    cleanTmp(); // clear leftovers from a previous crashed attempt
    const { vectorizeIndex } = await import("./vectorize.js");
    const result = await vectorizeIndex(ctx, { dbPath: tmpDb, allowDownload: true });

    // Consolidate: checkpoint the WAL into the main file and switch the temp db
    // to a rollback journal, so there are NO -wal/-shm sidecars to carry across
    // the publish (memory-extended forces WAL; this undoes it for a clean,
    // single-file publish). better-sqlite3 is present whenever the add-on is; a
    // runtime-resolved specifier keeps it off the build's type graph.
    const sqliteSpecifier = "better-sqlite3";
    const mod = (await import(sqliteSpecifier)) as {
      default: new (p: string) => { pragma(s: string): unknown; close(): void };
    };
    const db = new mod.default(tmpDb);
    try {
      db.pragma("wal_checkpoint(TRUNCATE)");
      db.pragma("journal_mode = DELETE");
    } finally {
      db.close();
    }
    if (existsSync(tmpDb + "-wal")) {
      throw new Error("temp index retained a WAL sidecar after consolidation; refusing to publish");
    }

    // Atomic, no-clobber publish: `linkSync` fails with EEXIST if finalDb already
    // exists (e.g. a concurrent `/recall` just built it), so we NEVER overwrite a
    // db someone else published — there is no check-then-write window. On success
    // finalDb is a hard link to the finished temp data; drop the temp name. We
    // deliberately do NOT touch finalDb's sidecars: when finalDb is absent any
    // are harmless orphans, and deleting them after publish could race a
    // `/recall` that just opened finalDb. The published db is in rollback-journal
    // mode, so it needs no sidecars of its own.
    try {
      linkSync(tmpDb, finalDb);
    } catch (e) {
      if ((e as NodeJS.ErrnoException).code === "EEXIST") {
        cleanTmp();
        out("recall index already present — nothing to do\n");
        return;
      }
      throw e;
    }
    rmSync(tmpDb, { force: true }); // remove the temp name; finalDb (the link) stays
    out(
      `recall index built — semantic search ready ` +
        `(memories: ${result.memories}, conversation chunks: ${result.sessionChunks})\n`,
    );
  } catch (e) {
    cleanTmp(); // leave finalDb absent → next session retries
    err(`[vortex] recall setup failed (will retry next session): ${(e as Error)?.message ?? e}\n`);
  } finally {
    lock.release();
  }
}

/**
 * `vortex vectorize --refresh` — the INCREMENTAL background worker session start
 * spawns once the recall index exists (see {@link spawnVectorizeRefresh}). Unlike
 * {@link runVectorizeSetup} there is NO model download and NO temp-db/publish
 * dance: the index is already built, so this folds newly-archived sessions and
 * changed memories straight into it, in place, cache-only.
 *
 * Safe to run concurrently with session starts and `/recall`:
 *   - it shares the SAME atomic lock as setup, so a build and a refresh (or two
 *     refreshes) can never write the index at once; `null` lock → another worker
 *     is already running, so we skip;
 *   - the index db uses WAL, so a concurrent `/recall` reader is never blocked;
 *   - if the embedding model is not cached the incremental pass throws fast
 *     (cache-only) and we treat it as a no-op — the explicit setup path owns the
 *     download. A throw NEVER escapes (best-effort): the next session retries.
 *
 * If the index does not exist yet there is nothing to refresh — the setup path
 * builds it — so we return quietly.
 */
async function runVectorizeRefresh(
  repoRoot: string,
  out: (s: string) => void,
  err: (s: string) => void,
): Promise<void> {
  const ctx = makeContext(repoRoot);
  const finalDb = join(ctx.dataDir, "_indexes", "memory.sqlite");
  if (!existsSync(finalDb)) {
    out("recall index not built yet — nothing to refresh\n");
    return;
  }

  // Acquiring the lock can itself throw (e.g. an fs error creating `_indexes`).
  // Honor this worker's best-effort contract: NEVER let a throw escape — a failed
  // refresh must self-heal next session, never surface as an unhandled rejection.
  let lock: VectorizeLock | null;
  try {
    lock = acquireVectorizeLock(ctx);
  } catch (e) {
    err(`[vortex] recall refresh skipped (could not acquire lock): ${(e as Error)?.message ?? e}\n`);
    return;
  }
  if (!lock) {
    out("a recall index build/refresh is already in progress — skipping\n");
    return;
  }
  try {
    const { vectorizeIndex } = await import("./vectorize.js");
    // Cache-only (allowDownload defaults false): never trigger a download here.
    const result = await vectorizeIndex(ctx);
    out(
      `recall index refreshed (memories: ${result.memories}, ` +
        `new conversation chunks: ${result.sessionChunks})\n`,
    );
  } catch (e) {
    // Model/add-on/index unavailable — skip; an explicit `/recall` rebuilds.
    err(`[vortex] recall refresh skipped: ${(e as Error)?.message ?? e}\n`);
  } finally {
    lock.release();
  }
}

/**
 * `vortex session-start` — the read-only start-of-session boot report. Mirrors
 * the `SessionStart` hook (`scripts/session-start-hook.mjs`): honor
 * `autoRecord.sessionStart`, resolve an environment label, fast-forward pull
 * when a remote exists (conflicts reported, never resolved; push never run),
 * detect days with commits but no worklog, fold transcripts into the archive
 * (catch-up, when the add-on is present), and render the report to stdout.
 */
/**
 * Layer-1 invisible-sync auto-seal. When the session-start ff-pull fails because
 * the branch DIVERGED (both machines committed) AND every LOCAL (unpushed)
 * diverged commit is an archive-only commit, the local side is cosmetic churn:
 * archive files carry a unique per-session UUID, so replaying them onto the
 * remote tip touches files no human edits, and in practice never conflicts.
 * Replay the local commits onto the remote with a rebase so the user is never
 * asked to reconcile churn they did not create. Returns the calm summary on a
 * successful seal, or null when not applicable (the caller then reports a
 * conflict).
 *
 * Only the LOCAL side must be archive-only (broadened 2026-06-18). The common
 * cross-machine case is one machine accruing only conversation archives while the
 * OTHER ships real work (docs, a release); the remote then carries human commits,
 * which the original "both sides archive-only" gate refused — leaving a "diverged"
 * report every session. Replaying our archive commits onto that remote is exactly
 * the manual pull the user would otherwise run by hand, so it is safe to automate.
 * A NON-archive commit on OUR side still defers to the user: that is their own
 * work, and only they decide how to reconcile it.
 *
 * The classifier is path-only, so "conflict-free" is a strong-in-practice
 * property, not a proof: a remote commit COULD in principle touch the same
 * archive path our local commit does (UUID filenames make this near-impossible,
 * but it is not structurally prevented). That case is safe by the abort path
 * below, not by the no-conflict claim — a conflicting rebase is caught, aborted
 * (HEAD restored on the clean tree), and we return null, falling back to the same
 * "diverged" report the old gate produced. So the worst case of the broadened
 * gate is a rare wasted rebase attempt that self-heals — never data loss or an
 * auto-resolved conflict (Codex review, 2026-06-18).
 *
 * Conservative by construction — every concern from the 2026-06-17 Codex review
 * is closed here:
 *   - **Pre-existing interrupted op → never touch.** If the repo is already
 *     mid-rebase/-merge/-cherry-pick, return null up front: that state is the
 *     user's (or another tool's) to finish, and our cleanup below must only ever
 *     undo state WE created.
 *   - **Frozen target (no TOCTOU).** Fetch ONCE, resolve `@{upstream}` to a
 *     concrete OID, classify against that OID, and rebase onto that SAME OID —
 *     never `git pull` (which would re-fetch and could splice in newer,
 *     unvalidated, possibly-human commits between the check and the rebase).
 *   - **Clean tree only (no autostash).** Auto-seal only when there are no
 *     uncommitted changes to tracked files (untracked files such as not-yet-
 *     committed transcripts are fine — rebase tolerates them, and at session
 *     start the tracked tree is normally clean). A dirty tree is handed to the
 *     user instead of being autostashed across a rebase — which removes the
 *     "stash fails to re-apply after history was already rewritten" failure mode
 *     entirely.
 *   - **State-aware rollback.** On rebase trouble, abort ONLY if a rebase is
 *     actually in progress (one we just started); with a clean tree that fully
 *     restores the pre-rebase HEAD.
 * A process kill mid-rebase can still leave rebase state — the existing
 * interrupted-op carryover signal surfaces that next session; the mutation
 * window is minimized (clean tree + unique files = a fast, conflict-free rebase).
 */
export function attemptArchiveOnlyAutoseal(
  repoRoot: string,
  archiveRel: string,
): { summary: string } | null {
  try {
    // A pre-existing interrupted op is not ours to clean up.
    if (detectInterruptedGitOp(repoRoot)) return null;

    // Freeze the target: fetch once, pin the exact upstream OID, and use it for
    // BOTH the classification and the rebase — no `git pull` re-fetch in between.
    gitNet(repoRoot, ["fetch"], 30_000);
    const upstream = gitOut(repoRoot, ["rev-parse", "@{upstream}"]).trim();
    if (!upstream) return null;
    // Pin the local side too, so a concurrent op moving HEAD between this
    // classification and the rebase below cannot make us replay unvalidated
    // local commits (Codex round 2, 2026-06-17).
    const headAtCheck = gitOut(repoRoot, ["rev-parse", "HEAD"]).trim();

    const [a, b] = gitOut(repoRoot, ["rev-list", "--left-right", "--count", `HEAD...${upstream}`])
      .trim()
      .split(/\s+/);
    const ahead = Number(a ?? 0) || 0;
    const behind = Number(b ?? 0) || 0;
    if (ahead === 0 || behind === 0) return null; // not a true divergence

    // Classify only OUR side. Replaying archive-only commits (unique per-session
    // files) onto the remote tip is conflict-free in practice whatever the remote
    // contains, so the only side that must be archive-only is the LOCAL one we
    // rebase; a rare same-path overlap aborts cleanly below (no data loss). A
    // non-archive commit on our side still defers to the user.
    const localDiverged = gitOut(repoRoot, ["rev-list", `${upstream}..HEAD`])
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (localDiverged.length === 0) return null;
    if (!localDiverged.every((sha) => isArchiveOnlyCommit(repoRoot, sha, archiveRel))) {
      return null; // human work on our side — the user decides how to reconcile
    }

    // Clean tree only: refuse if any TRACKED file has uncommitted changes
    // (`--untracked-files=no` ignores pending transcripts, which rebase tolerates).
    if (gitOut(repoRoot, ["status", "--porcelain", "--untracked-files=no"]).trim()) {
      return null;
    }

    // Final pre-mutation re-check, immediately before the one mutating call: bail
    // if HEAD moved or any git op appeared since we classified. This shrinks the
    // concurrency window to near-zero; it does not erase the inherent TOCTOU (a
    // git-level lock would), but the rest of the session-start git path accepts
    // the same sub-second race. (Codex round 2, 2026-06-17.)
    if (gitOut(repoRoot, ["rev-parse", "HEAD"]).trim() !== headAtCheck) return null;
    if (detectInterruptedGitOp(repoRoot)) return null;

    try {
      gitNet(repoRoot, ["rebase", upstream], 30_000); // onto the exact validated OID
      return {
        summary: `synced across machines — replayed ${ahead} local archive commit(s) onto ${behind} remote commit(s) (clean rebase)`,
      };
    } catch {
      // Roll back ONLY a rebase we actually started; with a clean tree this fully
      // restores HEAD. If none is in progress (rebase refused to start), there is
      // nothing to abort — fall through to the conflict report.
      if (detectInterruptedGitOp(repoRoot)) {
        try {
          gitOut(repoRoot, ["rebase", "--abort"]);
        } catch {
          // best-effort
        }
      }
      return null;
    }
  } catch {
    return null;
  }
}

/**
 * Diagnose WHY a fast-forward pull failed, for the start report. Read-only — it
 * never mutates anything; it only turns the opaque "diverged or dirty tree" into
 * an actionable line. The distinction matters because the two causes need
 * opposite remedies: a true divergence is reconciled (merge/rebase), while a
 * dirty tree is usually a perpetually-rewritten machine-local file (e.g. an
 * editor's view-state) that should be untracked + gitignored — and which silently
 * blocks BOTH the ff-pull and the archive-only auto-seal every session until it
 * is. Naming the blocking tracked file(s) is what lets the agent apply the
 * durable fix instead of re-reconciling the same churn forever.
 */
export function describePullFailure(repoRoot: string): string {
  const fallback = "fast-forward pull failed (diverged or dirty tree)";
  try {
    // Tracked files with uncommitted changes — untracked files (pending
    // transcripts) are excluded, matching the auto-seal's own clean-tree gate.
    // `core.quotepath=false` keeps non-ASCII paths (e.g. Korean filenames)
    // readable in the report instead of octal-escaped.
    const dirty = gitOut(repoRoot, [
      "-c",
      "core.quotepath=false",
      "status",
      "--porcelain",
      "--untracked-files=no",
    ])
      .split(/\r?\n/)
      .map((l) => l.slice(3).replace(/\r$/, "")) // strip the 2-char XY status + space
      .filter(Boolean);

    let ahead = 0;
    let behind = 0;
    try {
      const [a, b] = gitOut(repoRoot, ["rev-list", "--left-right", "--count", "HEAD...@{upstream}"])
        .trim()
        .split(/\s+/);
      ahead = Number(a) || 0;
      behind = Number(b) || 0;
    } catch {
      // no upstream resolvable — leave counts at 0
    }

    const parts: string[] = [];
    if (ahead > 0 && behind > 0) parts.push(`branch diverged (${ahead} local / ${behind} remote)`);
    else if (behind > 0) parts.push(`behind upstream by ${behind}`);
    if (dirty.length) {
      const shown = dirty.slice(0, 3).join(", ");
      const more = dirty.length > 3 ? ` (+${dirty.length - 3} more)` : "";
      parts.push(`uncommitted changes to tracked file(s): ${shown}${more}`);
    }
    return parts.length ? `fast-forward pull failed — ${parts.join("; ")}` : fallback;
  } catch {
    return fallback;
  }
}

async function runSessionStart(repoRoot: string, out: (s: string) => void): Promise<void> {
  const ctx = makeContext(repoRoot);
  const config = loadVortexConfig(ctx);
  if (!config.autoRecord.sessionStart) return; // disabled via vortex.json

  const environment = resolveSessionEnvironment(ctx, config);

  // A stale `.git/index.lock` left by a crashed/killed prior session (common
  // after an unattended delegated run whose terminal closed) blocks BOTH the
  // pull below AND the archive auto-commit further down — surfacing every later
  // session as a false "diverged / uncommitted" warning until it is deleted by
  // hand. Reclaim it FIRST when provably stale (see reclaimStaleGitIndexLock); a
  // live op is never clobbered, and a genuine interrupted merge/rebase is left
  // alone. Best-effort: never block startup on the reclaim itself.
  // Conservative reclaim here (NOT holding the archive `.sync.lock`): a bare peek
  // at a dead-holder `.sync.lock` is not enough to safely delete a still-FRESH git
  // lock — a concurrent live git op could hold it. So this early reclaim stays
  // age-TTL-only. The ownership-backed dead-holder reclaim runs inside
  // runArchiveSync below (which HOLDS the .sync.lock) and is surfaced afterwards.
  let lockReclaimedMs: number | null = null;
  try {
    lockReclaimedMs = reclaimStaleGitIndexLock(repoRoot);
  } catch {
    // best-effort — a reclaim failure must never break session start
  }

  // Fast-forward pull when a remote exists. Conflicts reported, never resolved.
  // gitNet (30s bound): the start hook BLOCKS session startup, so a stalled
  // remote must degrade to "pull failed" instead of hanging the whole start.
  let git: { ran: boolean; summary: string; conflict: boolean } | null = null;
  try {
    const remotes = gitOut(repoRoot, ["remote"]).trim();
    if (remotes) {
      try {
        const pulled = gitNet(repoRoot, ["pull", "--ff-only"], 30_000);
        const lastLine = pulled.trim().split(/\r?\n/).pop() || "up to date";
        git = { ran: true, summary: lastLine, conflict: false };
      } catch {
        // ff-pull failed: the branch diverged or the tree is dirty. First try
        // the Layer-1 archive-only auto-seal — silent reconcile when the only
        // divergence is conversation-archive churn from another machine. Only
        // when that doesn't apply do we surface a conflict for the user.
        const sealed = attemptArchiveOnlyAutoseal(repoRoot, archiveRelPath(ctx));
        git = sealed
          ? { ran: true, summary: sealed.summary, conflict: false }
          : {
              ran: true,
              summary: describePullFailure(repoRoot),
              conflict: true,
            };
      }
    }
  } catch {
    // not a git repo — skip silently
  }

  // Tier-1 carryover signals (cheap, git-only): work left over from a prior
  // session — surfaced in the report below. (Tier 2 is the `/resume` skill; see
  // decision-log 2026-06-04-session-recovery-two-tier.) Framework bookkeeping
  // is auto-maintained plumbing, not the user's WIP, so it is excluded from the
  // carryover count: the ownership manifest under `data/.vortex/`, and the
  // conversation archive under `data/_session-archive/` (auto-committed by the
  // archive sync below — and even when that commit is off or fails, archive
  // files are bookkeeping, not work the user left behind).
  const bookkeepingPrefix = frameworkBookkeepingPrefix(ctx);
  const archivePrefix = archiveRelPath(ctx) + "/";
  let carryover = collectCarryover(
    repoRoot,
    (p) => p.startsWith(bookkeepingPrefix) || p.startsWith(archivePrefix),
  );

  // Auto-reindex the on-demand memory index when stale (mechanical, idempotent;
  // gated by autoRecord.reindex). Runs AFTER pull (a freshly-pulled memory gets
  // indexed) and BEFORE collecting the report (staleness is computed during
  // collection, so the report reflects the fresh index). Skipped when the pull
  // diverged — never write into a tree the user must reconcile first.
  if (config.autoRecord.reindex && !git?.conflict) {
    await autoReindexMemory(ctx);
  }

  const report = await collectSessionStartReport(ctx, { environment });

  // Backfill detection: days with commits but no worklog (git-based; no LLM) —
  // the agent reconstructs those worklogs (AI-RULES.md "Default behaviors").
  // Gated on worklog + backfill being on, and on a clean pull (don't surface a
  // backfill target while the tree is diverged; the same day may already be
  // logged on the unmerged side).
  let missingWorklogDays: string[] = [];
  if (config.autoRecord.worklog && config.autoRecord.backfill && !git?.conflict) {
    try {
      // Count HUMAN activity only: a day whose commits are all archive-only
      // auto-syncs (mid-session ticks can produce many) holds no work to
      // backfill — counting them would flag phantom "missing worklog" days.
      const commitDays = humanCommitDates(repoRoot, gapWindowSinceArg(), archiveRelPath(ctx));
      missingWorklogDays = detectWorklogGaps(commitDays, report.recentWorklogDates);
    } catch {
      // no git / no history — skip gap detection
    }
  }

  // Archive sync: fold not-yet-archived transcripts into the local search
  // archive, sweep expired raw copies, and auto-commit the archive pathspec so
  // archived conversations never surface as uncommitted noise. Best-effort and
  // lock-guarded (a concurrent session's sync makes this one skip; the next
  // start repeats it). A lean base install (no memory-extended) still runs the
  // sweep/commit steps — only the ingest is skipped.
  let archiveSync: ArchiveSyncResult | null = null;
  if (config.autoRecord.archive) {
    try {
      archiveSync = await runArchiveSync(ctx, config, "start");
    } catch {
      // never block session start on the archive
    }
  }
  const catchUp = archiveSync?.catchUp ?? null;

  // The archive sync runs WHILE HOLDING its `.sync.lock`, so its reclaim of a
  // stale `.git/index.lock` is ownership-backed (unlike the bare early peek above).
  // When it cleared the lock the pre-sync carryover snapshot saw, surface the calm
  // 🔓 line and drop the now-stale ⚠️ — the lock is gone and the archive committed.
  if (archiveSync?.gitLockReclaimedMs != null) {
    lockReclaimedMs ??= archiveSync.gitLockReclaimedMs;
    if (carryover?.interrupted === "index.lock") {
      carryover = { ...carryover, interrupted: null };
    }
  }

  // Hand-off tidy: sweep hand-offs older than the retention window into
  // `_handoff/_archive/` (deterministic, age-based — git keeps the history, so
  // this is a working-tree tidy, not a destructive delete). Best-effort; a
  // failure here must never break session start.
  let handoffPrune: { archived: number } | null = null;
  if (config.autoRecord.handoff) {
    try {
      handoffPrune = await pruneHandoffs(ctx.dataDir, {
        now: new Date(),
        retentionDays: config.autoRecord.handoffRetentionDays,
      });
    } catch {
      // best-effort tidy — ignore
    }
  }

  // Auto-vectorize so semantic recall (and the ambient "did we discuss this?"
  // path) stays current — ALWAYS in a DETACHED background worker, NEVER on the
  // session-start critical path:
  //  - index already built → spawn the INCREMENTAL refresh worker (`vortex
  //    vectorize --refresh`): cache-only, folds new sessions/memories in without a
  //    download. This used to run inline and `await`ed, which hung session start
  //    for minutes once a heavy user's corpus grew — the whole point of the
  //    detach is that start returns immediately and the index catches up after.
  //  - index not built yet but the add-on is installed and auto-download is on →
  //    installing the add-on is the opt-in, so spawn the first-time setup worker
  //    that downloads the model (~470 MB, once) and builds the index.
  //  - a worker is already running, or no add-on / auto-download off → nothing; a
  //    manual `vortex vectorize` / `/recall` still sets it up.
  let vectorizeRefreshStarted = false;
  let vectorizeSetupStarted = false;
  if (config.autoRecord.vectorize) {
    const dbExists = existsSync(join(ctx.dataDir, "_indexes", "memory.sqlite"));
    const action = decideVectorizeAction({
      vectorizeOn: true,
      dbExists,
      autoDownloadOn: vectorizeAutoDownloadEnabled(config),
      // Only pay the resolve check when we might spawn setup (db missing).
      addonPresent: dbExists ? true : memoryExtendedPresent(),
      setupInProgress: vectorizeSetupInProgress(ctx),
    });
    if (action === "spawn-incremental") {
      try {
        spawnVectorizeRefresh(repoRoot);
        vectorizeRefreshStarted = true;
      } catch {
        // spawn failed — best effort; next session retries, or a manual `/recall`
      }
    } else if (action === "spawn-setup") {
      try {
        spawnVectorizeSetup(repoRoot);
        vectorizeSetupStarted = true;
      } catch {
        // spawn failed — best effort; a manual `vortex vectorize` still works
      }
    }
  }

  // Neural-voice model auto-download (mirrors the recall model download above):
  // when the computer-use add-on is installed but its Supertonic voice model is
  // not cached yet, kick off a DETACHED background fetch (~380 MB, once) so the
  // higher-quality voice is the default without the user ever running the fetch
  // command. Never blocks session start; the voice falls back to the built-in
  // Windows voice until the model lands. Opt out with `ttsAutoDownload: false`
  // or `VORTEX_CU_TTS_AUTO_DOWNLOAD=0`.
  let ttsDownloadStarted = false;
  {
    const modelPresent = supertonicModelPresent();
    const ttsAction = decideTtsDownloadAction({
      autoDownloadOn: ttsAutoDownloadEnabled(config),
      modelPresent,
      // Only pay the resolve check when we might spawn (model missing).
      addonPresent: modelPresent ? true : computerUsePresent(),
      setupInProgress: ttsDownloadInProgress(),
    });
    if (ttsAction === "spawn-setup") {
      try {
        ttsDownloadStarted = spawnTtsDownload(repoRoot);
      } catch {
        // spawn failed — best effort; a manual fetch still works
      }
    }
  }

  // Source-map freshness (activation-first design, 2026-07-10): when the code
  // index EXISTS (having one is the opt-in) and a mapped git repo moved since
  // indexing, spawn the same detached background reindex `find` would — the
  // index quietly tracks the code without anyone remembering `index`. Lazy
  // import (node:sqlite, Node >= 22.5) inside try/catch: on an older runtime or
  // any error this is silently skipped — freshness is advisory, never blocking.
  try {
    const smDb = join(ctx.dataDir, "_indexes", "source-map.sqlite");
    if (existsSync(smDb)) {
      const { shouldSpawnRefresh } = await import("./source-map.js");
      // Cooldown-gated (one refresh attempt per window) so a persistently
      // failing index never becomes a spawn loop across session starts.
      if (await shouldSpawnRefresh(repoRoot)) {
        spawnDetachedWorker(repoRoot, ["source-map", "index"]);
      }
    }
  } catch {
    // best-effort — a manual `vortex source-map index` (or any `find`) still refreshes
  }

  // Failure-ledger recurrence warnings (gated by autoRecord.failures): read
  // `data/_failures/` and surface keys at 2+ open occurrences with their
  // escalation-ladder stage. Read-only and best-effort — a ledger problem must
  // never break session start.
  let failures: import("./failures.js").FailureReportSlice | null = null;
  if (config.autoRecord.failures) {
    try {
      const { scanFailures, failureReportSlice } = await import("./failures.js");
      const slice = failureReportSlice(await scanFailures(ctx.dataDir));
      if (slice.warnings.length > 0) failures = slice;
    } catch {
      // best-effort — skip the section
    }
  }

  // Ontos knowledge-graph status (optional @vortex-os/ontos add-on). Surfaced only when
  // the add-on is installed AND its index exists; best-effort — a missing/broken add-on
  // (or a corrupt index) degrades to no line. Gate on existsSync FIRST so an absent index
  // is free AND the OntosStore constructor (which CREATES the db) is never called for a
  // missing index. The agent acts on the line per the AI-RULES ontos guidance.
  let ontosStatus: {
    objects: number;
    relations: number;
    proposals: number;
    lastScan?: { mode: string; op: string; at: string } | null;
    worklogMining?: { total: number; mined: number; pending: number; pendingRecent: number; pendingOlder: number };
    extractionRun?: { kind: string; done?: number; total?: number; startedAt?: string } | null;
  } | null = null;
  let ontosNeedsBootstrap = false;
  let ontosAutoRebuilt = false;
  // Whether cross-vendor egress (sending a triple + short quote to a SECOND vendor for
  // verification) is opted in. Surfaced as a standing privacy notice so the user always
  // sees that some data may leave the host + how to turn it (and the vendor's training) off.
  let ontosCrossVendorEgress = false;
  try {
    const ontosDb = join(ctx.dataDir, "_indexes", "ontos.sqlite");
    // Import FIRST: success means the add-on is INSTALLED. If installed but the index
    // file is absent, ontos is dormant — the agent gets no 🧠 line and never acts. Flag
    // it so the report tells the agent to bootstrap the index (the missing activation
    // signal — a fresh install otherwise does nothing).
    const ontos = await import("@vortex-os/ontos");
    if (existsSync(ontosDb)) {
      // Auto-rebuild the TRUSTED graph when the records changed since the last rebuild
      // (2026-07-08 ask: records written during the day never reached the graph until
      // someone remembered to run rebuild-ontos — the one non-automatic index left).
      // Deterministic + idempotent + seconds-fast, writes only the disposable
      // machine-local index (mined proposals survive), so the safe default is to just
      // run it. Gated by autoRecord.ontosRebuild; skipped while a live extraction
      // batch runs (same sqlite file — don't fight the runner); wholly best-effort.
      // Concurrency note (Codex review): two session starts could race into the same
      // rebuild — the script's clear+upsert is ONE sqlite transaction, so writers
      // serialize; the loser at worst throws SQLITE_BUSY into the catch (no marker,
      // retried next session). No corruption path → no extra lock machinery.
      if (config.autoRecord.ontosRebuild) {
        try {
          const script = resolveOntosRebuildScript(ctx.repoRoot);
          const marker = join(ctx.dataDir, "_indexes", ".ontos-rebuild.json");
          const liveRun = (() => {
            try {
              const f = (ontos as { enrich?: Record<string, unknown> }).enrich?.["readExtractionRun"];
              return typeof f === "function" ? (f as (d: string) => unknown)(ctx.dataDir) : null;
            } catch {
              return null;
            }
          })();
          const fp = ontosRecordsFingerprint(ctx.dataDir);
          let lastFp = "";
          let failedFp = "";
          try {
            const j = JSON.parse(readFileSync(marker, "utf8")) as { fp?: unknown; failedFp?: unknown };
            if (typeof j.fp === "string") lastFp = j.fp;
            if (typeof j.failedFp === "string") failedFp = j.failedFp;
          } catch {
            /* no/torn marker — treat as never rebuilt (a rebuild is idempotent) */
          }
          // failedFp: a persistently-failing rebuild must not stall EVERY session start
          // for the full timeout — retry only when the records changed again.
          if (!liveRun && script && fp !== lastFp && fp !== failedFp) {
            try {
              execFileSync(process.execPath, [script, "--data-dir", ctx.dataDir], {
                stdio: "ignore",
                timeout: 30_000,
                windowsHide: true,
              });
              // Stamp AFTER a successful rebuild (pre-scan fingerprint: an edit landing
              // mid-rebuild changes the fingerprint → re-runs next session).
              writeFileSync(marker, JSON.stringify({ fp, at: new Date().toISOString() }), "utf8");
              ontosAutoRebuilt = true;
            } catch {
              try {
                writeFileSync(marker, JSON.stringify({ fp: lastFp, failedFp: fp, at: new Date().toISOString() }), "utf8");
              } catch {
                /* marker unwritable — worst case the next start retries */
              }
            }
          }
        } catch {
          /* best-effort — a rebuild failure must never block the session report */
        }
      }
      const store = new ontos.sqlite.OntosStore(ontosDb);
      try {
        ontosStatus = store.counts();
        // Last scan status (extraction_scan_status) — guard the call: base may
        // run alongside an older installed ontos without this method.
        const s = store as unknown as { getScanStatus?: () => { mode?: unknown; op?: unknown; at?: unknown } | null };
        if (typeof s.getScanStatus === "function") {
          try {
            const raw = s.getScanStatus();
            // Shape-validate before it reaches the renderer (which slices `at`):
            // a corrupt row from a foreign/older index must not crash the report.
            if (
              raw &&
              typeof raw.mode === "string" &&
              typeof raw.op === "string" &&
              typeof raw.at === "string"
            ) {
              ontosStatus.lastScan = { mode: raw.mode, op: raw.op, at: raw.at };
            }
          } catch {
            /* status unreadable — the line just omits it */
          }
        }
        // Tiered-extraction status (2026-07-06): worklog-mining backlog, present only
        // when the installed ontos ships the status helpers (older add-on → omitted).
        // Guarded like getScanStatus — base must run alongside any ontos version.
        const en = (ontos as { enrich?: Record<string, unknown> }).enrich;
        const enrichmentStatusFn = en?.["enrichmentStatus"];
        if (ontosStatus && typeof enrichmentStatusFn === "function") {
          try {
            const es = enrichmentStatusFn(store, ctx.dataDir, { recentDays: 10, categories: ["worklog"] }) as {
              categories?: {
                worklog?: { total?: unknown; enriched?: unknown; pending?: unknown; pendingRecent?: unknown; pendingOlder?: unknown };
              };
            };
            const w = es?.categories?.worklog;
            if (w && typeof w.total === "number" && typeof w.enriched === "number" && typeof w.pending === "number") {
              ontosStatus.worklogMining = {
                total: w.total,
                mined: w.enriched,
                pending: w.pending,
                pendingRecent: typeof w.pendingRecent === "number" ? w.pendingRecent : 0,
                pendingOlder: typeof w.pendingOlder === "number" ? w.pendingOlder : 0,
              };
            }
          } catch {
            /* backlog probe failed — the report simply omits the mining line */
          }
        }
      } finally {
        store.close();
      }
      // Live extraction batch (run marker with fresh heartbeat + live pid): progress
      // beats proposal in the report. Reads a small JSON file — no store needed.
      const en2 = (ontos as { enrich?: Record<string, unknown> }).enrich;
      const readRunFn = en2?.["readExtractionRun"];
      if (ontosStatus && typeof readRunFn === "function") {
        try {
          const run = readRunFn(ctx.dataDir) as { kind?: unknown; done?: unknown; total?: unknown; startedAt?: unknown } | null;
          if (run && typeof run.kind === "string") {
            ontosStatus.extractionRun = {
              kind: run.kind,
              done: typeof run.done === "number" ? run.done : undefined,
              total: typeof run.total === "number" ? run.total : undefined,
              startedAt: typeof run.startedAt === "string" ? run.startedAt : undefined,
            };
          }
        } catch {
          /* marker unreadable — treated as not running */
        }
      }
    } else {
      ontosNeedsBootstrap = true; // installed but never built
    }
    // Read the egress opt-in from .agent/vortex.json (fail-closed to false on any error).
    try {
      ontosCrossVendorEgress = ontos.crossVendorEgressFromConfig(ctx.dataDir) === true;
    } catch {
      ontosCrossVendorEgress = false;
    }
  } catch {
    // add-on absent / broken / corrupt index — no ontos line
  }

  // Update lifecycle — signal 1 (local, no network): are there framework
  // template updates the installed package has but this instance hasn't applied
  // yet? A dry-run of the templates update is a cheap local hash compare; shown
  // once per session so an available `vortex update` is never silently missed.
  let templateUpdate: { pending: number; conflicts: number; toVersion?: string } | null = null;
  try {
    const td = resolveTemplatesDir();
    if (td) {
      const dry = await runTemplatesUpdate(ctx, td, { dryRun: true });
      if (dry.status !== "no-manifest" && dry.status !== "no-templates") {
        const pending = dry.summary.replaced + dry.summary.restored + dry.summary.installed;
        if (pending > 0 || dry.summary.conflicts > 0) {
          templateUpdate = { pending, conflicts: dry.summary.conflicts, toVersion: dry.toVersion };
        }
      }
    }
  } catch {
    // best-effort — a check failure must never block the session report
  }

  // Update lifecycle — signal 2 (network): once per session (this hook fires
  // once per session), check the npm registry for a newer @vortex-os/base. On
  // by default; `updates.check: "off"` disables it with zero network contact.
  // Offline/failure → silent (checkBaseUpdate never throws and returns
  // newer:false, which the renderer skips).
  let updateCheck: ReturnType<typeof checkBaseUpdate> | null = null;
  if (config.updates.check !== "off") {
    try {
      updateCheck = checkBaseUpdate(ctx);
    } catch {
      // never block the session report on an update check
    }
  }

  // Add-on freshness (network): same once-per-session gate as the base check.
  // Surfaces only add-ons that are BEHIND their latest npm version (and whether a
  // version pin blocks the update — the 0.x caret trap). Offline/failure → empty
  // (silent); read-only, never auto-bumps.
  let addonsBehind: ReturnType<typeof checkAddonUpdates> | null = null;
  if (config.updates.check !== "off") {
    try {
      addonsBehind = checkAddonUpdates(ctx).filter((s) => s.behind);
    } catch {
      // never block the session report on an add-on check
    }
  }

  // Global-usage offer (existing-user path): if this machine has not enabled
  // "VortEX from any folder" and the user has not declined, offer it once per
  // session. Reads the global ~/.claude state; never blocks the report.
  let globalSetupOffer = false;
  try {
    const gs = inspectGlobalSetup(undefined, repoRoot);
    globalSetupOffer = !gs.done && !gs.declined;
  } catch {
    // never block the session report on the global-setup probe
  }

  // Installed framework version — local, network-free; always shown in the report.
  const baseVersion = readInstalledBaseVersion();

  out(
    renderSessionStartReport(report, {
      git,
      lockReclaimedMs: lockReclaimedMs ?? undefined,
      baseVersion,
      missingWorklogDays,
      catchUp: catchUp ?? undefined,
      archiveSync: archiveSync
        ? {
            committed: archiveSync.committed,
            pushed: archiveSync.pushed,
            pushSkipped: archiveSync.pushSkipped,
            rawPruned: archiveSync.rawPruned,
            retentionDays: archiveSync.retentionDays,
          }
        : undefined,
      vectorizeRefresh: vectorizeRefreshStarted || undefined,
      ontos: ontosStatus ?? undefined,
      ontosAutoRebuilt: ontosAutoRebuilt || undefined,
      ontosNeedsBootstrap: ontosNeedsBootstrap || undefined,
      ontosCrossVendorEgress: ontosCrossVendorEgress || undefined,
      vectorizeSetup: vectorizeSetupStarted || undefined,
      ttsDownloadSetup: ttsDownloadStarted || undefined,
      templateUpdate: templateUpdate ?? undefined,
      updateCheck: updateCheck ?? undefined,
      addonsBehind: addonsBehind && addonsBehind.length > 0 ? addonsBehind : undefined,
      globalSetupOffer: globalSetupOffer || undefined,
      carryover: carryover ?? undefined,
      handoffPrune: handoffPrune ?? undefined,
      failures: failures ?? undefined,
    }),
  );
}

/**
 * `vortex session-end` — best-effort archive sync at wind-down (gated by
 * `autoRecord.archive` + `autoRecord.archiveAtEnd`): ingest closed sessions,
 * sweep expired raw copies, auto-commit the archive pathspec, and — only with
 * `sync.autoPush` opted in — push.
 *
 * Finalize (0.14.2): the ENDING session itself is now included. The host pipes
 * the hook payload into stdin; when it names a `transcript_path`, the hook
 * waits for that file to stop changing (size+mtime stable ~1.2s, capped 12s —
 * typically ~1.5s total) and lowers the ingest live-guard to the transcript's
 * verified age, so the day's last session is archived and pushed before the
 * process exits instead of waiting for tomorrow's session-start.
 *
 * Since the self-detach (see session-end-detach.ts) this function is normally
 * the DETACHED CHILD's body — the hook invocation returns immediately and the
 * child does the work with stdin re-piped to it. It still runs inline for a
 * TTY stdin or under VORTEX_SESSION_END_INLINE (which the child itself sets).
 *
 * Still a NET, not a guarantee: no payload / an unstable file / a killed
 * terminal / crash all degrade to the pre-0.14.2 behavior, and session-start
 * repeats every step — a failure or skip here is always silent and harmless.
 * Self-heal is unchanged: transcript bytes the host appends after the hook
 * advance the source mtime and the next catch-up re-ingests the complete copy.
 *
 * Deliberately lean otherwise (a SessionEnd hook should finish in seconds): no
 * report, no reindex, no vectorize, no update checks.
 *
 * History: this used to drop an empty placeholder worklog, which backfired (a
 * stub by date masks the very gap SessionStart's `detectWorklogGaps` should
 * flag), then became a pure no-op; the worklog net lives at SessionStart.
 */
async function runSessionEnd(repoRoot: string, out: (s: string) => void, hookPayload?: string): Promise<void> {
  try {
    const ctx = makeContext(repoRoot);
    const config = loadVortexConfig(ctx);
    if (!config.autoRecord.archive || !config.autoRecord.archiveAtEnd) return;

    // Finalize the ending session when the host tells us which one it is.
    // Absolute paths only — the host always sends one; a relative path would
    // resolve against whatever cwd the hook ran in, which is never intended.
    // stableForMs 500 (0.14.4, down from the 1.2s default): at exit the host
    // writes the transcript's last lines moments before this hook runs, so the
    // already-quiet credit is ~0 and the wait is paid IN FULL — out of the
    // same short kill-window grace the commit/push tail needs (one machine
    // missed the commit by about that much). 0.5s of proven quiet is enough
    // here because a straggler line only advances mtime and the next sync
    // re-ingests the complete copy (the standing self-heal). The 12s hard cap
    // stays for a transcript still being flushed.
    // `hookPayload` is the stdin the detach dispatcher already captured — it
    // arrives on the inline-fallback path, where process.stdin is drained.
    let finalize: { liveSessionSkipMs: number } | undefined;
    try {
      const payload = parseHookStdin(hookPayload ?? (await readHookStream(process.stdin)));
      const tp = payload.transcriptPath;
      if (tp && isAbsolute(tp) && existsSync(tp)) {
        const mtimeMs = await waitForTranscriptStability(tp, { stableForMs: 500 });
        if (mtimeMs !== null) finalize = { liveSessionSkipMs: liveSkipForFinalize(mtimeMs) };
      }
    } catch {
      // any stdin/stat oddity → plain sync, exactly the pre-finalize behavior
    }

    const r = await runArchiveSync(ctx, config, "end", finalize ? { catchUp: finalize } : undefined);
    if (r.lockSkipped) {
      out("archive sync skipped (another session is syncing)\n");
      return;
    }
    const parts: string[] = [];
    if (r.catchUp) parts.push(`${r.catchUp.ingestedLocal} archived`);
    if (r.rawPruned > 0) parts.push(`${r.rawPruned} raw pruned`);
    if (r.committed) parts.push("committed");
    if (r.pushed) parts.push("pushed");
    // Failure reasons stay visible: the 0.14.2 field diagnosis stalled on an
    // output that never said WHICH step skipped or why.
    if (!r.committed && r.commitSkipped && r.commitSkipped !== "nothing-to-commit") {
      parts.push(`commit skipped: ${r.commitSkipped}`);
    }
    if (!r.pushed && r.pushSkipped && r.pushSkipped !== "off" && r.pushSkipped !== "no-commit") {
      parts.push(`push skipped: ${r.pushSkipped}`);
    }
    // Honest label: the override was APPLIED; whether the ending session was
    // actually ingested shows in the archived count above.
    if (finalize) parts.push(`finalize guard ${finalize.liveSessionSkipMs}ms`);
    out(`session-end archive sync: ${parts.length ? parts.join(", ") : "nothing to do"}\n`);
  } catch {
    // never block shutdown on the archive
  }
}

/** The mid-session tick's throttle stamp (mtime = last successful tick sync). */
function tickStampPath(ctx: ModuleContext): string {
  return join(ctx.dataDir, "_session-archive", ".last-tick");
}

/**
 * Throttle check for the mid-session tick: due when the stamp is absent or
 * older than the configured interval. Pure on injected `nowMs` for tests.
 * Exported for tests.
 */
export function tickIsDue(stampPath: string, minutes: number, nowMs: number = Date.now()): boolean {
  if (!(minutes > 0)) return false;
  try {
    return nowMs - statSync(stampPath).mtimeMs >= minutes * 60_000;
  } catch {
    return true; // no stamp yet — first tick is due
  }
}

/**
 * `vortex session-tick` — the Stop-hook body for mid-session archive
 * streaming (opt-in via `autoRecord.archiveTickMinutes`; the hook is wired by
 * init/update only when enabled, and carries `async: true` so the user never
 * waits on it). At most once per configured interval it runs the archive sync
 * in the "tick" phase: ingest (this session admitted via the finalize-style
 * live-guard override — its transcript just settled when the turn ended),
 * commit the archive subtrees, and the STRICT archive-only push (see
 * pushArchiveOnly). While a session is alive there is no host kill window, so
 * a WAN push has all the time it needs — this is what makes the day's
 * conversations reach the remote continuously instead of betting everything
 * on the exit grace.
 *
 * Cost discipline: the not-due path must stay near-free (config + one stat),
 * because it runs after EVERY assistant turn.
 */
async function runSessionTick(repoRoot: string, out: (s: string) => void): Promise<void> {
  try {
    const ctx = makeContext(repoRoot);
    const config = loadVortexConfig(ctx);
    const minutes = config.autoRecord.archiveTickMinutes;
    if (!config.autoRecord.archive || minutes <= 0) return; // feature off — silent, fast
    const stamp = tickStampPath(ctx);
    if (!tickIsDue(stamp, minutes)) return; // not due — silent, fast

    // Admit THIS session's transcript (the host pipes its path into the Stop
    // hook): it just settled when the assistant turn completed, so the wait
    // is typically instant via the already-elapsed-quiet credit.
    let live: { liveSessionSkipMs: number } | undefined;
    try {
      const payload = parseHookStdin(await readHookStream(process.stdin));
      const tp = payload.transcriptPath;
      if (tp && isAbsolute(tp) && existsSync(tp)) {
        const mtimeMs = await waitForTranscriptStability(tp, { stableForMs: 500 });
        if (mtimeMs !== null) live = { liveSessionSkipMs: liveSkipForFinalize(mtimeMs) };
      }
    } catch {
      // any stdin/stat oddity → plain sync
    }

    const r = await runArchiveSync(ctx, config, "tick", live ? { catchUp: live } : undefined);
    if (r.lockSkipped) {
      out("session-tick: skipped (another sync is running)\n");
      return;
    }
    // Stamp only a run that actually ran — a skipped run must leave the next
    // turn's tick due, not silently swallow the interval.
    try {
      writeFileSync(stamp, new Date().toISOString() + "\n", "utf8");
    } catch {
      // stamp is best-effort; worst case the next tick runs early
    }
    const parts: string[] = [];
    if (r.catchUp) parts.push(`${r.catchUp.ingestedLocal} archived`);
    if (r.committed) parts.push("committed");
    if (r.pushed) parts.push("pushed");
    if (!r.committed && r.commitSkipped && r.commitSkipped !== "nothing-to-commit") {
      parts.push(`commit skipped: ${r.commitSkipped}`);
    }
    if (!r.pushed && r.pushSkipped && r.pushSkipped !== "off" && r.pushSkipped !== "no-commit") {
      parts.push(`push skipped: ${r.pushSkipped}`);
    }
    out(`session-tick archive sync: ${parts.length ? parts.join(", ") : "nothing to do"}\n`);
  } catch {
    // never disturb the live session
  }
}

/**
 * Commit dates (`--date=short`, newest first, one per commit) counting HUMAN
 * activity only: commits that touch nothing outside the archive subtrees are
 * the framework's own auto-syncs and are excluded. One git call for the whole
 * window; merge/empty commits (no listed paths) count as human, mirroring
 * isArchiveOnlyCommit's conservatism. Exported for tests.
 */
export function humanCommitDates(repoRoot: string, sinceArg: string, archiveRel: string): string[] {
  const raw = gitOut(repoRoot, [
    "log",
    `--since=${sinceArg}`,
    "--date=short",
    "--name-only",
    "--pretty=format:@@COMMIT@@ %cd",
  ]);
  const rawPrefix = `${archiveRel}/raw/`;
  const normPrefix = `${archiveRel}/normalized/`;
  const dates: string[] = [];
  let cur: string | null = null;
  let paths: string[] = [];
  const flush = (): void => {
    if (cur === null) return;
    const archiveOnly =
      paths.length > 0 && paths.every((p) => p.startsWith(rawPrefix) || p.startsWith(normPrefix));
    if (!archiveOnly) dates.push(cur);
  };
  for (const line of raw.split(/\r?\n/)) {
    if (line.startsWith("@@COMMIT@@ ")) {
      flush();
      cur = line.slice("@@COMMIT@@ ".length).trim();
      paths = [];
    } else if (line.trim()) {
      paths.push(line.trim());
    }
  }
  flush();
  return dates;
}

/**
 * `vortex catch-up` — the explicit, on-demand archive sync (same steps as the
 * hooks: ingest closed sessions → sweep raw → auto-commit → opt-in push). The
 * agent runs this FIRST when the user asks to sync, so the day's closed
 * sessions are archived and committed before the user's own commit/push flow;
 * only the still-open session(s) wait for the next hook. Prints a JSON summary.
 */
async function runCatchUpCli(repoRoot: string, out: (s: string) => void): Promise<void> {
  const ctx = makeContext(repoRoot);
  const config = loadVortexConfig(ctx);
  const r = await runArchiveSync(ctx, config, "manual");
  out(
    JSON.stringify(
      {
        ran: r.ran,
        lockSkipped: r.lockSkipped,
        ingested: r.catchUp?.ingestedLocal ?? 0,
        indexedPulled: r.catchUp?.indexedPulled ?? 0,
        ingestErrors: r.catchUp?.errors ?? 0,
        rawPruned: r.rawPruned,
        rawRetentionDays: r.retentionDays,
        committed: r.committed,
        ...(r.commitSkipped ? { commitSkipped: r.commitSkipped } : {}),
        pushed: r.pushed,
        ...(r.pushSkipped ? { pushSkipped: r.pushSkipped } : {}),
      },
      null,
      2,
    ) + "\n",
  );
}

// `gitOut` / `detectInterruptedGitOp` moved to ./archive-sync.js (the archive
// sync needs them and cli-dispatch imports archive-sync, so they live at the
// bottom of that dependency edge). Re-exported here so the package surface
// (`index.ts` → cli-dispatch) and existing tests keep working unchanged.
export { detectInterruptedGitOp, reclaimStaleGitIndexLock } from "./archive-sync.js";

/**
 * Assemble the Tier-1 carryover signal for the session-start report. The
 * interrupted-op check and the uncommitted count are computed INDEPENDENTLY: a
 * held/stale `index.lock` can make `git status` fail, and that must NOT suppress
 * the interrupted-op signal — the very case we most want to surface. Returns
 * null when there is nothing to report (clean tree / not a git repo). Exported
 * for tests. (decision-log 2026-06-04-session-recovery-two-tier.)
 */
export function collectCarryover(
  repoRoot: string,
  ignore?: (repoRelPosixPath: string) => boolean,
): { uncommitted: number; interrupted: string | null } | null {
  const interrupted = detectInterruptedGitOp(repoRoot); // self-guarded; never throws
  let uncommitted = 0;
  try {
    uncommitted = countUncommitted(gitOut(repoRoot, ["status", "--porcelain"]), ignore);
  } catch {
    // git status failed (a held index.lock, or not a git repo) — still surface
    // the interrupted signal computed above; just report 0 uncommitted.
  }
  return uncommitted > 0 || interrupted ? { uncommitted, interrupted } : null;
}

/** Environment label: config signal rules → VORTEX_ENV → `.agent/environment` first line. */
function resolveSessionEnvironment(ctx: ModuleContext, config: ReturnType<typeof loadVortexConfig>): string | null {
  let environment = resolveEnvironment(config, {
    hostname: hostname(),
    env: process.env,
    pathExists: existsSync,
  });
  if (!environment) environment = process.env.VORTEX_ENV?.trim() || null;
  if (!environment) {
    const envFile = join(ctx.repoRoot, ".agent", "environment");
    if (existsSync(envFile)) {
      environment = readFileSync(envFile, "utf8").split(/\r?\n/)[0]?.trim() || null;
    }
  }
  return environment;
}
